import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, BarChart, Bar } from 'recharts';

import type { SalesPoint, HourlyPoint } from './types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type SalesChartProps = {
  weekly: SalesPoint[];
  hourly: HourlyPoint[];
  currency: string;
  locale: string;
};

const buildCurrencyFormatter = (locale: string, currency = 'USD') =>
  new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 });

export function SalesChart({ weekly, hourly, currency, locale }: SalesChartProps) {
  const { t, i18n } = useTranslation('dashboard');
  const resolvedLocale = locale || i18n.language || 'en';

  const currencyFormatter = useMemo(
    () => buildCurrencyFormatter(resolvedLocale, currency || 'USD'),
    [currency, resolvedLocale],
  );

  const formatCurrency = (value: number) => currencyFormatter.format(value / 100);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t('salesChart.title')}</CardTitle>
        <CardDescription>{t('salesChart.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue="revenue" className="space-y-3">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="revenue" className="text-xs">{t('salesChart.tabs.revenue')}</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs">{t('salesChart.tabs.orders')}</TabsTrigger>
            <TabsTrigger value="hourly" className="text-xs">{t('salesChart.tabs.hourly')}</TabsTrigger>
          </TabsList>

          <TabsContent value="revenue" className="space-y-2">
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weekly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={formatCurrency} />
                  <RechartsTooltip
                    formatter={(value) => [formatCurrency(Number(value)), t('salesChart.tooltip.revenue')]}
                    labelFormatter={(label) => t('salesChart.tooltip.day', { label })}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="space-y-2">
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value: number) => [value, t('salesChart.tooltip.orders')]}
                    labelFormatter={(label) => t('salesChart.tooltip.day', { label })}
                  />
                  <Bar
                    dataKey="orders"
                    fill="hsl(var(--chart-1))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="hourly" className="space-y-2">
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <RechartsTooltip
                    formatter={(value: number) => [value, t('salesChart.tooltip.orders')]}
                    labelFormatter={(label) => t('salesChart.tooltip.time', { label })}
                  />
                  <Bar
                    dataKey="orders"
                    fill="hsl(var(--chart-2))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
